import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeftRight, 
  Building2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Download, 
  FileText,
  AlertTriangle,
  Loader2,
  Eye
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransferRequestsCard({ onTransferProcessed }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transfers, setTransfers] = useState({ received: [], sent: [] });
  const [processingId, setProcessingId] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, transfer: null });
  const [rejectReason, setRejectReason] = useState("");
  const [forceDialog, setForceDialog] = useState({ open: false, transfer: null });
  const [forceReason, setForceReason] = useState("");
  const [forceProofFile, setForceProofFile] = useState(null);
  const [viewProofDialog, setViewProofDialog] = useState({ open: false, transfer: null, proofUrl: null });

  useEffect(() => {
    fetchTransfers();
  }, []);

  const fetchTransfers = async () => {
    try {
      const response = await axios.get(`${API_URL}/enterprises/transfers/pending`);
      setTransfers(response.data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch transfers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transferId) => {
    setProcessingId(transferId);
    try {
      await axios.post(`${API_URL}/enterprises/transfers/${transferId}/approve`);
      toast.success("Transfert approuvé avec succès");
      fetchTransfers();
      if (onTransferProcessed) onTransferProcessed();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Veuillez indiquer un motif de refus");
      return;
    }
    
    setProcessingId(rejectDialog.transfer.id);
    try {
      await axios.post(
        `${API_URL}/enterprises/transfers/${rejectDialog.transfer.id}/reject?reason=${encodeURIComponent(rejectReason)}`
      );
      toast.success("Demande de transfert refusée");
      setRejectDialog({ open: false, transfer: null });
      setRejectReason("");
      fetchTransfers();
      if (onTransferProcessed) onTransferProcessed();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du refus");
    } finally {
      setProcessingId(null);
    }
  };

  const handleForceTransfer = async () => {
    if (!forceReason.trim()) {
      toast.error("Veuillez indiquer un motif pour forcer le transfert");
      return;
    }
    if (!forceProofFile) {
      toast.error("Veuillez joindre une preuve justifiant le forçage");
      return;
    }

    setProcessingId(forceDialog.transfer.id);
    try {
      const formData = new FormData();
      formData.append("reason", forceReason);
      formData.append("proof_document", forceProofFile);

      await axios.post(
        `${API_URL}/enterprises/transfers/${forceDialog.transfer.id}/force`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Transfert forcé avec succès");
      setForceDialog({ open: false, transfer: null });
      setForceReason("");
      setForceProofFile(null);
      fetchTransfers();
      if (onTransferProcessed) onTransferProcessed();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du forçage");
    } finally {
      setProcessingId(null);
    }
  };

  const downloadProof = async (transferId) => {
    try {
      const response = await axios.get(
        `${API_URL}/enterprises/transfers/${transferId}/proof`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from content-disposition header if available
      const contentDisposition = response.headers["content-disposition"];
      let filename = "preuve_transfert.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/['"]/g, "");
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Document téléchargé");
    } catch (error) {
      toast.error("Erreur lors du téléchargement du document");
    }
  };

  const totalPending = transfers.received.length + transfers.sent.length;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (totalPending === 0) {
    return null;
  }

  return (
    <>
      <Card className="border-2 border-amber-200 bg-amber-50" data-testid="transfer-requests-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-amber-600" />
              <CardTitle className="font-heading text-amber-800">
                Demandes de Transfert ({totalPending})
              </CardTitle>
            </div>
          </div>
          <CardDescription className="text-amber-700">
            Demandes en attente de transfert d'entreprises
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Demandes reçues - pour le cabinet propriétaire */}
          {transfers.received.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Demandes reçues ({transfers.received.length})
              </h4>
              <div className="space-y-3">
                {transfers.received.map((transfer) => (
                  <div
                    key={transfer.id}
                    className="bg-white border border-amber-200 rounded-lg p-4 shadow-sm"
                    data-testid={`transfer-received-${transfer.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="font-semibold">{transfer.enterprise_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Demandé par : <span className="font-medium">{transfer.requesting_cabinet_name}</span>
                        </p>
                        {transfer.reason && (
                          <p className="text-sm text-slate-600 mt-1 italic">
                            Motif : "{transfer.reason}"
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(transfer.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {transfer.proof_document_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadProof(transfer.id)}
                            data-testid={`download-proof-${transfer.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Preuve
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setRejectDialog({ open: true, transfer })}
                          disabled={processingId === transfer.id}
                          data-testid={`reject-transfer-${transfer.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(transfer.id)}
                          disabled={processingId === transfer.id}
                          data-testid={`approve-transfer-${transfer.id}`}
                        >
                          {processingId === transfer.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          )}
                          Approuver
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demandes envoyées - pour le cabinet demandeur */}
          {transfers.sent.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4" />
                {isAdmin ? "Toutes les demandes en attente" : "Demandes envoyées"} ({transfers.sent.length})
              </h4>
              <div className="space-y-3">
                {transfers.sent.map((transfer) => (
                  <div
                    key={transfer.id}
                    className="bg-white border border-amber-200 rounded-lg p-4 shadow-sm"
                    data-testid={`transfer-sent-${transfer.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="font-semibold">{transfer.enterprise_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {isAdmin ? (
                            <>
                              De : <span className="font-medium">{transfer.current_cabinet_name}</span>
                              {" → "}
                              <span className="font-medium">{transfer.requesting_cabinet_name}</span>
                            </>
                          ) : (
                            <>Cabinet actuel : <span className="font-medium">{transfer.current_cabinet_name}</span></>
                          )}
                        </p>
                        {transfer.reason && (
                          <p className="text-sm text-slate-600 mt-1 italic">
                            Motif : "{transfer.reason}"
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(transfer.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {transfer.proof_document_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadProof(transfer.id)}
                            data-testid={`download-proof-sent-${transfer.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir preuve
                          </Button>
                        )}
                        {isSuperAdmin && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setForceDialog({ open: true, transfer })}
                            disabled={processingId === transfer.id}
                            data-testid={`force-transfer-${transfer.id}`}
                          >
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            Forcer
                          </Button>
                        )}
                        {!isSuperAdmin && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            En attente
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de refus */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => {
        if (!open) {
          setRejectDialog({ open: false, transfer: null });
          setRejectReason("");
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Refuser le transfert
            </DialogTitle>
            <DialogDescription>
              Refuser la demande de transfert pour l'entreprise "{rejectDialog.transfer?.enterprise_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reject-reason" className="text-sm font-medium">
                Motif du refus <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reject-reason"
                placeholder="Expliquez pourquoi vous refusez ce transfert..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-2 min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, transfer: null })}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectReason.trim() || processingId === rejectDialog.transfer?.id}
            >
              {processingId === rejectDialog.transfer?.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Refuser le transfert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de forçage (Admin) */}
      <Dialog open={forceDialog.open} onOpenChange={(open) => {
        if (!open) {
          setForceDialog({ open: false, transfer: null });
          setForceReason("");
          setForceProofFile(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Forcer le transfert (Admin)
            </DialogTitle>
            <DialogDescription>
              Forcer le transfert de "{forceDialog.transfer?.enterprise_name}" vers "{forceDialog.transfer?.requesting_cabinet_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Attention :</strong> Cette action force le transfert sans l'accord du cabinet propriétaire actuel. 
                Vous devez justifier cette décision et joindre une preuve.
              </p>
            </div>
            <div>
              <Label htmlFor="force-reason" className="text-sm font-medium">
                Motif du forçage <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="force-reason"
                placeholder="Justifiez pourquoi vous forcez ce transfert..."
                value={forceReason}
                onChange={(e) => setForceReason(e.target.value)}
                className="mt-2 min-h-[100px]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">
                Document de preuve <span className="text-red-500">*</span>
              </Label>
              <div className="mt-2">
                {forceProofFile ? (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <span className="flex-1 text-sm text-emerald-700 truncate">
                      {forceProofFile.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForceProofFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label
                    htmlFor="force-proof-upload"
                    className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <FileText className="w-5 h-5 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      Cliquez pour joindre un document (PDF, JPG, PNG)
                    </span>
                  </label>
                )}
                <input
                  id="force-proof-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setForceProofFile(e.target.files[0])}
                  className="hidden"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setForceDialog({ open: false, transfer: null })}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleForceTransfer}
              disabled={!forceReason.trim() || !forceProofFile || processingId === forceDialog.transfer?.id}
            >
              {processingId === forceDialog.transfer?.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Forcer le transfert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
